import React, { useRef, useState, useEffect, useCallback } from "react";
import { Tooltip } from "src/assets/imports/antdComponents";

interface IRightAlignedEllipsisTextProps {
	text: string;
	maxLines?: number;
	lineHeight?: number;
	className?: string;
	/** Optional max width in px. When provided the container is capped at this
	 *  width and text truncation is recalculated against the narrower box.
	 *  Useful in multi-tile mode where y-axis labels must not crowd the chart. */
	maxWidth?: number;
}

export const RightAlignedEllipsisText: React.FC<IRightAlignedEllipsisTextProps> = ({ text, maxLines = 2, lineHeight = 16, className = "", maxWidth }) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const [displayText, setDisplayText] = useState(text);
	const [isTruncated, setIsTruncated] = useState(false);
	const [effectiveMaxLines, setEffectiveMaxLines] = useState(maxLines);

	const truncateText = useCallback(() => {
		const container = containerRef.current;
		if (!container) return;

		// Get parent container height to check available space
		const parentHeight = container.parentElement?.offsetHeight || 0;

		// Adjust maxLines if container is too short
		let adjustedMaxLines = maxLines;
		if (parentHeight > 0 && parentHeight < 3 * lineHeight && maxLines === 3) {
			adjustedMaxLines = 2;
		}

		setEffectiveMaxLines(adjustedMaxLines);
		const maxHeight = adjustedMaxLines * lineHeight;

		// Use the explicit maxWidth cap if provided, otherwise use the real container width
		const measuredWidth = maxWidth ?? container.offsetWidth;
		// Create temporary element to measure
		const temp = document.createElement("div");
		temp.style.cssText = `
			position: absolute;
			visibility: hidden;
			width: ${measuredWidth}px;
			font-size: inherit;
			font-family: inherit;
			font-weight: inherit;
			line-height: ${lineHeight}px;
			word-break: normal;
			overflow-wrap: normal;
		`;
		temp.textContent = text;
		document.body.appendChild(temp);

		if (temp.offsetHeight <= maxHeight) {
			// Text fits, no truncation needed
			setDisplayText(text);
			setIsTruncated(false);
			document.body.removeChild(temp);
			return;
		}

		// Binary search for the right length
		let low = 0;
		let high = text.length;
		let bestFit = text;

		while (low <= high) {
			const mid = Math.floor((low + high) / 2);
			const testText = text.substring(0, mid) + "...";
			temp.textContent = testText;

			if (temp.offsetHeight <= maxHeight) {
				bestFit = testText;
				low = mid + 1;
			} else {
				high = mid - 1;
			}
		}

		document.body.removeChild(temp);
		setDisplayText(bestFit);
		setIsTruncated(bestFit !== text);
	}, [text, maxLines, lineHeight, maxWidth]);

	useEffect(() => {
		// Delay truncation to ensure container is fully rendered
		const timer = setTimeout(truncateText, 0);
		window.addEventListener("resize", truncateText);

		return () => {
			clearTimeout(timer);
			window.removeEventListener("resize", truncateText);
		};
	}, [truncateText]);

	const textElement = (
		<div
			ref={containerRef}
			className={`text-end ${className}`}
			style={{
				lineHeight: `${lineHeight}px`,
				wordBreak: "break-word",
				maxHeight: `${effectiveMaxLines * lineHeight}px`,
				overflow: "hidden",
				// Cap the rendered width so the cell never grows beyond the budget
				...(maxWidth !== undefined ? { width: maxWidth, maxWidth, minWidth: 0 } : { width: "100%" }),
			}}>
			{displayText}
		</div>
	);

	if (!isTruncated) {
		return textElement;
	}

	return (
		<Tooltip
			title={text}
			placement="top"
			overlayStyle={{ maxWidth: 300 }}
			overlayInnerStyle={{ maxHeight: "20rem", overflowY: "auto" }}
			getPopupContainer={(triggerNode) => triggerNode.closest(".ant-table-body") || document.body}>
			{textElement}
		</Tooltip>
	);
};
